import { type ComputedRef, computed, inject, type Ref, ref } from "vue";
import { useRoute } from "vue-router";
import { DESTINATIONS, type Destination } from "@/shared/contracts/destinations";
import type { PageContext } from "../api/stream";

/**
 * Onde a pessoa está, para o turno saber do que ela está falando.
 *
 * ── POR QUE NÃO DENTRO DA DOCA ────────────────────────────────────────────
 *
 * A coluna do Ben na inbox faz a mesma pergunta e não pode ler a doca — nem um feature
 * ler outro (SPEC §6). Aqui é uma função sobre o `useRoute()`, que qualquer
 * superfície do painel pode chamar.
 *
 * ── O QUE MUDA COM ISTO ───────────────────────────────────────────────────
 *
 * O `recordId`. Nenhum lugar do front o mandava, e o servidor já o usava para
 * escopar a memória ao registro e para dizer em que etapa do funil ele está — de
 * modo que "o que é isto?" numa ficha custava uma tool call de descoberta para
 * reencontrar o que a barra de endereço já dizia.
 *
 * ── POR QUE PELO `name` DA ROTA ───────────────────────────────────────────
 *
 * Comparar caminho é reescrever o roteador em `startsWith`. Os nomes estão em
 * `app/routes.ts` e mudam junto com as rotas; uma rota renomeada quebra aqui de
 * uma vez, em vez de silenciosamente parar de reconhecer a tela.
 */
export interface PageContextApi {
	readonly page: ComputedRef<PageContext>;
	readonly destinations: Ref<readonly Destination[]>;
}

/** Nada provido é uma resposta legítima: fora da moldura do painel não há menu. */
const NONE: Ref<readonly Destination[]> = ref([]);

/**
 * Os três ids do contrato são `z.uuid()` no servidor, e o schema do turno é
 * ESTRITO: um `/e/pedido/rascunho` mandado como `recordId` não seria ignorado —
 * derrubaria a pergunta inteira com um erro de validação. Um id que não é um id
 * simplesmente não viaja.
 */
/** O formato de `agent.code` (back, 0014). */
const AGENT_CODE = /^[a-z][a-z0-9_]{1,62}$/;

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function usePageContext(): PageContextApi {
	const route = useRoute();
	const destinations = inject(DESTINATIONS, NONE);

	const page = computed<PageContext>(() => {
		const name = String(route.name ?? "");
		const param = (key: string): string | undefined => {
			const value = route.params[key];
			const one = Array.isArray(value) ? value[0] : value;
			return one ? String(one) : undefined;
		};
		const id = (): string | undefined => {
			const value = param("id");
			return value && UUID.test(value) ? value : undefined;
		};

		return {
			path: route.path,
			title: titleOf(route.path, route.meta.title, destinations.value),
			...(name === "entity" || name === "entity-record" ? { entity: param("entityCode") } : {}),
			...(name === "entity-record" ? { recordId: id() } : {}),
			...(name === "contact" ? { contactId: id() } : {}),
			...(name === "inbox" ? { conversationId: id() } : {}),
			// O agente aberto: o Ben lê o conhecimento próprio dele. Só no feitio de um
			// código (o servidor valida, e um inválido derrubaria a pergunta).
			...(name === "agents" && AGENT_CODE.test(param("code") ?? "")
				? { agentCode: param("code") }
				: {}),
			// A versão do site vem da QUERY (`?site=`, escrita pelo SiteBuilderPage), não
			// do caminho: `/site` é uma tela só, e com duas versões o Ben precisa saber
			// de qual se fala. Mesma regra dos ids acima — só viaja se for uuid.
			...(name === "site" && typeof route.query.site === "string" && UUID.test(route.query.site)
				? { siteId: route.query.site }
				: {}),
		};
	});

	return { page, destinations };
}

/**
 * O nome da tela, com as palavras do menu.
 *
 * Sem segundo dicionário de nomes: quem já nomeia toda tela do painel é
 * `nav-items.ts`, e as entidades se nomeiam sozinhas. O prefixo mais longo é o
 * que faz `/e/pedido/<uuid>` responder "Pedidos" e não "Começar" — `/` casaria
 * com tudo, e por isso está fora da comparação por prefixo.
 */
function titleOf(path: string, meta: unknown, destinations: readonly Destination[]): string {
	if (typeof meta === "string" && meta) return meta;

	let best: Destination | undefined;
	for (const item of destinations) {
		const matches = item.to === path || (item.to !== "/" && path.startsWith(`${item.to}/`));
		if (matches && (!best || item.to.length > best.to.length)) best = item;
	}
	return best?.label ?? path;
}
